'use client';

import React from 'react';
import { 
  Search, 
  ChevronDown, 
  ScanLine, 
  Plus, 
  MoreVertical,
  Folder,
  Sparkles,
  Package,
  Eye,
  ArrowUpDown
} from 'lucide-react';

interface ProductsFilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (cat: string) => void;
  brands: string[];
  selectedBrand: string;
  onBrandChange: (brand: string) => void;
  selectedStockFilter: string;
  onStockFilterChange: (stock: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  selectedSort: string;
  onSortChange: (sort: string) => void;
  onOpenStockEntry: () => void;
  onOpenNewProduct: () => void;
  onOpenImportMenu?: () => void;
}

export default function ProductsFilterBar({
  searchTerm,
  onSearchChange,
  categories,
  selectedCategory,
  onCategoryChange,
  brands,
  selectedBrand,
  onBrandChange,
  selectedStockFilter,
  onStockFilterChange,
  selectedStatus,
  onStatusChange,
  selectedSort,
  onSortChange,
  onOpenStockEntry,
  onOpenNewProduct,
  onOpenImportMenu
}: ProductsFilterBarProps) {
  return (
    <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2.5">
      {/* Buscador reactivo */}
      <div className="relative flex-1 min-w-[240px]">
        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
          <Search className="w-4 h-4" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar por nombre, SKU o precio..."
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200/90 rounded-2xl text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition"
        />
      </div>

      {/* Dropdowns de Filtrado y Botones de Acción */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Dropdown Categoría */}
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="appearance-none bg-white border border-slate-200/90 rounded-2xl pl-3 pr-7 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm cursor-pointer transition"
          >
            <option value="ALL">Categoría: Todas</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Dropdown Marca */}
        <div className="relative">
          <select
            value={selectedBrand}
            onChange={(e) => onBrandChange(e.target.value)}
            className="appearance-none bg-white border border-slate-200/90 rounded-2xl pl-3 pr-7 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm cursor-pointer transition"
          >
            <option value="ALL">Marca: Todas</option>
            {brands.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Dropdown Stock */}
        <div className="relative">
          <select
            value={selectedStockFilter}
            onChange={(e) => onStockFilterChange(e.target.value)}
            className="appearance-none bg-white border border-slate-200/90 rounded-2xl pl-3 pr-7 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm cursor-pointer transition"
          >
            <option value="ALL">Stock: Todos</option>
            <option value="IN_STOCK">Con stock</option>
            <option value="LOW_STOCK">Stock bajo / crítico</option>
            <option value="OUT_OF_STOCK">Sin stock</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Dropdown Estado */}
        <div className="relative">
          <select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className="appearance-none bg-white border border-slate-200/90 rounded-2xl pl-3 pr-7 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm cursor-pointer transition"
          >
            <option value="ALL">Estado: Todos</option>
            <option value="ACTIVE">Activos</option>
            <option value="INACTIVE">Pausados</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Dropdown Orden */}
        <div className="relative">
          <select
            value={selectedSort}
            onChange={(e) => onSortChange(e.target.value)}
            className="appearance-none bg-white border border-slate-200/90 rounded-2xl pl-3 pr-7 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm cursor-pointer transition"
          >
            <option value="NAME_ASC">⇅ Orden: Nombre (A-Z)</option>
            <option value="PRICE_DESC">⇅ Mayor precio</option>
            <option value="PRICE_ASC">⇅ Menor precio</option>
            <option value="STOCK_ASC">⇅ Menor stock</option>
            <option value="STOCK_DESC">⇅ Mayor stock</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Botón Negro Ingreso de Stock */}
        <button
          onClick={onOpenStockEntry}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-2xl shadow-sm transition"
          title="Recepción de mercadería física"
        >
          <ScanLine className="w-3.5 h-3.5 text-emerald-400" />
          <span>Ingreso de stock</span>
        </button>

        {/* Botón Negro + Producto */}
        <button
          onClick={onOpenNewProduct}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-2xl shadow-sm transition"
        >
          <Plus className="w-4 h-4 stroke-[2.5]" />
          <span>Producto</span>
        </button>

        {/* Menú de Opciones Adicionales ⋮ */}
        <button
          onClick={onOpenImportMenu}
          className="p-2 bg-white border border-slate-200/90 hover:border-slate-300 text-slate-600 rounded-2xl shadow-sm transition"
          title="Importar catálogo desde Excel"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
